import { useEffect } from 'react';

/**
 * SEOHead Component - Updates document head for SEO
 * Use this component on individual pages to set page-specific meta tags
 * 
 * @param {string} title - Page title
 * @param {string} description - Page description (max 160 chars recommended)
 * @param {string} path - Page path (e.g., '/product/123')
 * @param {string} image - OG image URL (optional)
 * @param {string} type - OG type (default: 'website')
 * @param {string} keywords - Comma-separated keywords (optional)
 * @param {object} schema - JSON-LD schema object (optional)
 */
function SEOHead({ 
  title, 
  description, 
  path = '', 
  image = 'https://theailedger.ai/og-image.png',
  type = 'website',
  keywords = '',
  schema = null,
  // When true, tell search engines to skip indexing this page and not
  // follow its links. Use on soft-404 states (deleted product, missing
  // blog post, unknown slug) so Google Search Console stops flagging
  // them as "Soft 404" — the HTTP status stays 200 because we're an
  // SPA, but the noindex meta is the officially recommended signal.
  noindex = false,
}) {
  const siteUrl = 'https://theailedger.ai';
  // Normalize canonical: strip trailing slashes, drop hash, and drop most
  // query params — but preserve `category` so /featured?category=X is
  // treated as its own indexable URL by search engines. Add other
  // SEO-meaningful params here as we introduce them.
  const SEO_INDEXABLE_PARAMS = new Set(['category']);
  const splitOnQuery = (path || '').split('?');
  const cleanPath = splitOnQuery[0].split('#')[0].replace(/\/+$/, '');
  let canonicalPath = cleanPath;
  if (splitOnQuery[1]) {
    try {
      const incoming = new URLSearchParams(splitOnQuery[1].split('#')[0]);
      const kept = new URLSearchParams();
      for (const [key, val] of incoming.entries()) {
        if (SEO_INDEXABLE_PARAMS.has(key) && val) kept.append(key, val);
      }
      const qs = kept.toString();
      if (qs) canonicalPath += `?${qs}`;
    } catch {
      // Malformed query — fall back to bare path; never break the page.
    }
  }
  const fullUrl = `${siteUrl}${canonicalPath}`;
  const fullTitle = title ? `${title} | theailedger.ai` : 'theailedger.ai - The Marketplace for AI Products';
  const metaDescription = description || 'Discover, buy, and sell cutting-edge AI tools and products. The premier marketplace for AI-powered solutions.';
  const baseKeywords = 'AI marketplace, AI tools, AI products, buy AI tools, sell AI products, theailedger';
  const fullKeywords = keywords ? `${keywords}, ${baseKeywords}` : baseKeywords;

  useEffect(() => {
    document.title = fullTitle;

    const updateMetaTag = (selector, attribute, value) => {
      let element = document.querySelector(selector);
      if (element) {
        element.setAttribute(attribute, value);
      } else {
        element = document.createElement('meta');
        if (selector.includes('property=')) {
          element.setAttribute('property', selector.match(/property="([^"]+)"/)[1]);
        } else if (selector.includes('name=')) {
          element.setAttribute('name', selector.match(/name="([^"]+)"/)[1]);
        }
        element.setAttribute(attribute, value);
        document.head.appendChild(element);
      }
    };

    // Primary meta tags
    updateMetaTag('meta[name="description"]', 'content', metaDescription);
    updateMetaTag('meta[name="title"]', 'content', fullTitle);
    updateMetaTag('meta[name="keywords"]', 'content', fullKeywords);
    
    // Canonical URL
    let canonical = document.querySelector('link[rel="canonical"]');
    if (canonical) {
      canonical.setAttribute('href', fullUrl);
    } else {
      canonical = document.createElement('link');
      canonical.setAttribute('rel', 'canonical');
      canonical.setAttribute('href', fullUrl);
      document.head.appendChild(canonical);
    }

    // Open Graph tags — Phase 3 audit fix. The previous set was missing
    // og:site_name, og:image:alt, image dimensions, and twitter:card type,
    // which is why the audit flagged Social Share Cards 28/100. Slack/LinkedIn/
    // Twitter/Facebook all need these to render a proper preview card.
    updateMetaTag('meta[property="og:title"]', 'content', fullTitle);
    updateMetaTag('meta[property="og:description"]', 'content', metaDescription);
    updateMetaTag('meta[property="og:url"]', 'content', fullUrl);
    updateMetaTag('meta[property="og:image"]', 'content', image);
    updateMetaTag('meta[property="og:image:alt"]', 'content', fullTitle);
    updateMetaTag('meta[property="og:image:width"]', 'content', '1200');
    updateMetaTag('meta[property="og:image:height"]', 'content', '630');
    updateMetaTag('meta[property="og:type"]', 'content', type);
    updateMetaTag('meta[property="og:site_name"]', 'content', 'theailedger.ai');
    updateMetaTag('meta[property="og:locale"]', 'content', 'en_US');

    // Twitter tags — `summary_large_image` is what triggers the big preview
    // card. Without it Twitter falls back to a tiny thumbnail or no card.
    updateMetaTag('meta[name="twitter:card"]', 'content', 'summary_large_image');
    updateMetaTag('meta[name="twitter:site"]', 'content', '@theailedger');
    updateMetaTag('meta[name="twitter:creator"]', 'content', '@theailedger');
    updateMetaTag('meta[property="twitter:title"]', 'content', fullTitle);
    updateMetaTag('meta[property="twitter:description"]', 'content', metaDescription);
    updateMetaTag('meta[property="twitter:url"]', 'content', fullUrl);
    updateMetaTag('meta[property="twitter:image"]', 'content', image);
    updateMetaTag('meta[property="twitter:image:alt"]', 'content', fullTitle);

    // Robots — explicit index/follow + max-snippet/max-image-preview so
    // Google can show the full headline-length snippet (Google Snippet
    // Quality 42/100 fix). Callers that render a soft-404 state (product
    // deleted, blog post missing, unknown slug) can flip `noindex` to
    // opt this page out — that stops Google flagging it as Soft 404.
    updateMetaTag(
      'meta[name="robots"]',
      'content',
      noindex
        ? 'noindex, nofollow'
        : 'index, follow, max-snippet:-1, max-image-preview:large, max-video-preview:-1'
    );

    // JSON-LD Schema
    if (schema) {
      let scriptTag = document.querySelector('script[type="application/ld+json"]');
      if (scriptTag) {
        scriptTag.textContent = JSON.stringify(schema);
      } else {
        scriptTag = document.createElement('script');
        scriptTag.type = 'application/ld+json';
        scriptTag.textContent = JSON.stringify(schema);
        document.head.appendChild(scriptTag);
      }
    }

    return () => {
      document.title = 'theailedger.ai - The Marketplace for AI Products';
    };
  }, [fullTitle, metaDescription, fullUrl, fullKeywords, image, type, schema, noindex]);

  return null;
}

export default SEOHead;

// Pre-built schema generators
export const generateProductSchema = (product, storeUrl) => ({
  '@context': 'https://schema.org',
  '@type': 'Product',
  name: product.name,
  description: product.description,
  image: product.images?.[0] || '',
  url: storeUrl,
  offers: {
    '@type': 'Offer',
    price: product.price,
    priceCurrency: 'USD',
    availability: 'https://schema.org/InStock',
    seller: {
      '@type': 'Organization',
      name: product.store_name || 'theailedger.ai'
    }
  },
  aggregateRating: product.rating ? {
    '@type': 'AggregateRating',
    ratingValue: product.rating,
    reviewCount: product.total_reviews || 0
  } : undefined,
  category: product.category
});

export const generateOrganizationSchema = () => ({
  '@context': 'https://schema.org',
  '@type': 'Organization',
  name: 'theailedger.ai',
  alternateName: 'The AI Ledger',
  url: 'https://theailedger.ai',
  logo: 'https://theailedger.ai/logo.png',
  description: 'The premier online marketplace for AI-powered digital products. Connecting AI creators, developers, and entrepreneurs with businesses and individuals seeking cutting-edge AI solutions.',
  foundingDate: '2025',
  sameAs: [
    'https://twitter.com/theailedger',
    'https://www.instagram.com/theailedger.ai',
    'https://www.tiktok.com/@theailedger.ai'
  ],
  contactPoint: {
    '@type': 'ContactPoint',
    email: 'support@theailedger.ai',
    telephone: '+1-253-215-9534',
    contactType: 'customer support',
    availableLanguage: ['English', 'Spanish', 'French', 'German', 'Portuguese', 'Japanese', 'Korean', 'Chinese']
  },
  knowsAbout: [
    'Artificial Intelligence', 'AI Tools', 'AI Software', 'Machine Learning',
    'AI Marketplace', 'Digital Products', 'AI Automation', 'AI Chatbots',
    'Prompt Engineering', 'AI Business Solutions'
  ]
});

export const generateWebsiteSchema = () => ({
  '@context': 'https://schema.org',
  '@type': 'WebSite',
  name: 'theailedger.ai',
  alternateName: 'The AI Ledger',
  url: 'https://theailedger.ai',
  description: 'The premier marketplace for buying and selling AI-powered digital products, tools, and solutions. Browse AI products across 52 categories from creators worldwide.',
  potentialAction: {
    '@type': 'SearchAction',
    // Use a dedicated /search?q= path so the SearchAction template can never
    // collide with the homepage (Googlebot has historically crawled the
    // literal placeholder when it shared the homepage URL, generating
    // ghost "Alternate page" notices in Search Console).
    target: {
      '@type': 'EntryPoint',
      urlTemplate: 'https://theailedger.ai/search?q={search_term_string}'
    },
    'query-input': 'required name=search_term_string'
  },
  publisher: {
    '@type': 'Organization',
    name: 'theailedger.ai'
  }
});

export const generateMarketplaceSchema = () => ({
  '@context': 'https://schema.org',
  '@type': 'Store',
  name: 'theailedger.ai',
  url: 'https://theailedger.ai',
  description: 'Online marketplace for AI tools, AI software, AI prompts, AI agents, and digital AI products. Buy and sell AI-powered solutions.',
  image: 'https://theailedger.ai/og-image.png',
  priceRange: '$',
  currenciesAccepted: 'USD',
  paymentAccepted: 'Credit Card, PayPal, Stripe',
  areaServed: 'Worldwide',
  hasOfferCatalog: {
    '@type': 'OfferCatalog',
    name: 'AI Products Catalog',
    itemListElement: [
      { '@type': 'OfferCatalog', name: 'AI Business Tools' },
      { '@type': 'OfferCatalog', name: 'AI Writing Tools' },
      { '@type': 'OfferCatalog', name: 'AI Image Generation' },
      { '@type': 'OfferCatalog', name: 'AI Video Tools' },
      { '@type': 'OfferCatalog', name: 'AI Chatbots & Agents' },
      { '@type': 'OfferCatalog', name: 'AI Automation' },
      { '@type': 'OfferCatalog', name: 'AI Marketing Tools' },
      { '@type': 'OfferCatalog', name: 'AI Coding Tools' },
      { '@type': 'OfferCatalog', name: 'AI Data Analytics' },
      { '@type': 'OfferCatalog', name: 'Prompt Engineering' }
    ]
  }
});

export const generateLaunchEventSchema = () => ({
  '@context': 'https://schema.org',
  '@type': 'Event',
  name: 'theailedger.ai Marketplace Launch',
  description: 'The official launch of theailedger.ai — the premier marketplace for AI-powered digital products and tools. Browse, buy, and sell AI solutions from creators worldwide.',
  startDate: '2026-06-03T12:00:00Z',
  eventStatus: 'https://schema.org/EventScheduled',
  eventAttendanceMode: 'https://schema.org/OnlineEventAttendanceMode',
  location: {
    '@type': 'VirtualLocation',
    url: 'https://theailedger.ai'
  },
  organizer: {
    '@type': 'Organization',
    name: 'theailedger.ai',
    url: 'https://theailedger.ai'
  },
  offers: {
    '@type': 'Offer',
    price: '0',
    priceCurrency: 'USD',
    availability: 'https://schema.org/InStock',
    url: 'https://theailedger.ai'
  }
});

export const generateFAQSchema = (faqs) => ({
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: faqs.map(faq => ({
    '@type': 'Question',
    name: faq.question,
    acceptedAnswer: {
      '@type': 'Answer',
      text: faq.answer
    }
  }))
});

/**
 * BreadcrumbList schema — required for AI Overviews + Google rich results.
 * Pass an array of {name, path} (path is relative, e.g. "/featured").
 *
 *   generateBreadcrumbSchema([
 *     { name: 'Home', path: '/' },
 *     { name: 'Featured AI Tools', path: '/featured' },
 *     { name: 'Granola', path: '/featured/granola' },
 *   ])
 */
export const generateBreadcrumbSchema = (items) => ({
  '@context': 'https://schema.org',
  '@type': 'BreadcrumbList',
  itemListElement: items.map((it, idx) => ({
    '@type': 'ListItem',
    position: idx + 1,
    name: it.name,
    item: `https://theailedger.ai${it.path || ''}`,
  })),
});

/**
 * SoftwareApplication schema for a featured AI tool's detail page. Used
 * on /featured/:slug so AI Overviews / Perplexity / ChatGPT can cite the
 * tool name + category + provider + offer link inside an answer.
 */
export const generateSoftwareApplicationSchema = (partner) => {
  if (!partner) return null;
  const url = partner.affiliate_url
    || (partner.slug ? `https://theailedger.ai/api/affiliate-partners/go/${partner.slug}` : 'https://theailedger.ai');
  const logo = partner.logo_url
    ? (partner.logo_url.startsWith('http') ? partner.logo_url : `https://theailedger.ai${partner.logo_url}`)
    : undefined;
  return {
    '@context': 'https://schema.org',
    '@type': 'SoftwareApplication',
    name: partner.name,
    applicationCategory: partner.category || 'BusinessApplication',
    operatingSystem: 'Web',
    description: partner.tagline || (partner.description || '').slice(0, 300),
    url,
    ...(logo && { image: logo }),
    offers: {
      '@type': 'Offer',
      price: partner.starting_price || '0',
      priceCurrency: 'USD',
      availability: 'https://schema.org/InStock',
      url,
    },
    provider: {
      '@type': 'Organization',
      name: partner.name,
      ...(partner.website && { url: partner.website }),
    },
    isPartOf: {
      '@type': 'WebSite',
      name: 'theailedger.ai',
      url: 'https://theailedger.ai',
    },
  };
};

/**
 * Service schema — for the public /optimizer lead magnet. Tells AI
 * Overviews + Google that we offer a free AI listing audit service.
 */
export const generateOptimizerServiceSchema = () => ({
  '@context': 'https://schema.org',
  '@type': 'Service',
  name: 'Free AI Tool Listing Optimizer',
  serviceType: 'AI Listing SEO Audit',
  url: 'https://theailedger.ai/optimizer',
  description: 'Free instant AI listing audit. Paste your AI tool URL and we score SEO, trust signals, pricing clarity, and return a conservative monthly earnings range. No signup required.',
  provider: {
    '@type': 'Organization',
    name: 'theailedger.ai',
    url: 'https://theailedger.ai',
  },
  areaServed: 'Worldwide',
  audience: {
    '@type': 'BusinessAudience',
    audienceType: 'AI software creators, indie SaaS founders, AI product makers',
  },
  offers: {
    '@type': 'Offer',
    price: '0',
    priceCurrency: 'USD',
    availability: 'https://schema.org/InStock',
    url: 'https://theailedger.ai/optimizer',
  },
});
